import {
  Bot,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  Globe2,
  PanelLeftClose,
  PanelLeftOpen,
  Square,
  StickyNote,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Tip } from "@/components/ui/overlays";
import { ScrollArea } from "@/components/ui/primitives";
import { keys, useVaultTree } from "@/hooks/queries";
import { useUpload } from "@/hooks/use-upload";
import { api, queryClient, type NodeOut, type VaultEntry } from "@/lib/api";
import { NODE_COLOR } from "@/lib/palette";
import { cn } from "@/lib/utils";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

/** VS-Code / Obsidian-style file explorer over the on-device vault
 * (`~/GraphMind`) — the real folders and files, not a synthetic view.
 * Files the app knows open in-app: documents in the reader, notes, AI outputs
 * and org hubs in the markdown editor, anything else focused in the atlas. */
export function FileTreePanel({
  embedded,
  onClose,
}: {
  /** Rendered inside a mobile drawer: fill it, and close it (not collapse). */
  embedded?: boolean;
  onClose?: () => void;
} = {}) {
  const [open, setOpen] = useState(true);
  const tree = useVaultTree();
  const upload = useUpload();
  const [creating, setCreating] = useState(false);
  const [folderName, setFolderName] = useState("");
  const dirInputRef = useRef<HTMLInputElement>(null);

  // `webkitdirectory` isn't a typed React prop; set it on the real input so the
  // picker selects a whole folder (each File then carries webkitRelativePath).
  useEffect(() => {
    const el = dirInputRef.current;
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
  }, []);

  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) return;
    try {
      await api.request("POST", "/api/vault/folders", { path: name });
      setFolderName("");
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: keys.vault });
      toast.success(`Created folder “${name}”`);
    } catch (e) {
      toast.error("Could not create folder", { description: String(e) });
    }
  };

  if (!embedded && !open) {
    return (
      <Tip label="Open file explorer" side="right">
        <button
          onClick={() => setOpen(true)}
          className="z-20 flex w-9 shrink-0 flex-col items-center gap-2 border-r border-border pt-3 text-muted-foreground hover:text-foreground"
          aria-label="Open file explorer"
        >
          <PanelLeftOpen className="h-4 w-4" />
          <span className="font-mono text-[9px] uppercase tracking-widest [writing-mode:vertical-rl]">
            files
          </span>
        </button>
      </Tip>
    );
  }

  return (
    <aside
      className={cn(
        "z-20 flex shrink-0 flex-col bg-card/40",
        embedded ? "h-full w-full" : "w-[238px] border-r border-border",
      )}
    >
      <div className="hairline-b flex h-11 shrink-0 items-center gap-2 px-3">
        <span className="font-display text-[13px] italic text-foreground">Files</span>
        {tree.data?.root && (
          <span className="truncate font-mono text-[9px] text-muted-foreground" title={tree.data.root}>
            {tree.data.root.replace(/\\/g, "/").split("/").pop()}
          </span>
        )}
        <Tip label="New folder">
          <button
            onClick={() => setCreating((c) => !c)}
            className="ml-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="New folder"
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </button>
        </Tip>
        <Tip label="Import a folder (keeps its structure)">
          <button
            onClick={() => dirInputRef.current?.click()}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Import a folder (keeps its structure)"
          >
            <FolderUp className="h-3.5 w-3.5" />
          </button>
        </Tip>
        <Tip label={embedded ? "Close" : "Collapse"}>
          <button
            onClick={() => (embedded ? onClose?.() : setOpen(false))}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={embedded ? "Close" : "Collapse"}
          >
            {embedded ? <X className="h-4 w-4" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
          </button>
        </Tip>
      </div>

      {/* Hidden folder picker (whole-directory upload, structure preserved). */}
      <input
        ref={dirInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) void upload(e.target.files);
          e.target.value = "";
        }}
      />

      {creating && (
        <div className="hairline-b flex items-center gap-1.5 px-3 py-1.5">
          <Folder className="h-3.5 w-3.5 shrink-0 text-primary/70" />
          <input
            autoFocus
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void createFolder();
              if (e.key === "Escape") {
                setCreating(false);
                setFolderName("");
              }
            }}
            placeholder="folder name (use / to nest)…"
            className="min-w-0 flex-1 rounded border border-border bg-input px-1.5 py-0.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
      )}

      <ScrollArea className="min-h-0 flex-1">
        <div className="py-1.5 pr-2">
          {(tree.data?.tree ?? []).map((e) => (
            <TreeRow key={e.relpath} entry={e} depth={0} onNavigate={embedded ? onClose : undefined} />
          ))}
          {tree.isSuccess && tree.data.tree.length === 0 && (
            <p className="px-3 py-8 text-center font-display text-sm italic leading-relaxed text-muted-foreground">
              Your vault is empty — drop in a PDF to begin.
            </p>
          )}
          {tree.isSuccess && !tree.data.enabled && (
            <p className="px-3 py-8 text-center text-xs leading-relaxed text-muted-foreground">
              The local vault is disabled in settings.
            </p>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}

function fileIcon(entry: VaultEntry) {
  const kind = entry.ref?.kind;
  const nodeType = entry.ref?.node_type;
  if (nodeType === "org")
    return <Square className="h-3.5 w-3.5 shrink-0" style={{ color: NODE_COLOR.org }} />;
  if (nodeType === "note") return <StickyNote className="h-3.5 w-3.5 shrink-0 text-skyb" />;
  if (nodeType === "ai_output") return <Bot className="h-3.5 w-3.5 shrink-0 text-orchid" />;
  if (entry.name.toLowerCase().endsWith(".html") || entry.relpath.includes("web"))
    return <Globe2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  if (kind === "document") return <FileText className="h-3.5 w-3.5 shrink-0 text-primary/80" />;
  return <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

function TreeRow({
  entry,
  depth,
  onNavigate,
}: {
  entry: VaultEntry;
  depth: number;
  /** Fired after a file opens — lets the mobile drawer close itself. */
  onNavigate?: () => void;
}) {
  // Top-level category folders start open, nested ones closed — like a
  // freshly-opened VS Code workspace.
  const [expanded, setExpanded] = useState(depth === 0);

  const openFile = async () => {
    const ref = entry.ref;
    if (!ref) {
      toast(entry.name, { description: "This file isn't linked to the atlas." });
      return;
    }
    try {
      if (ref.kind === "document") {
        useTabs.getState().openReader(ref.id, entry.name.replace(/\.[^.]+$/, ""));
        onNavigate?.();
        return;
      }
      // A markdown-backed node (note / AI output / org hub) opens in the rich
      // markdown editor so it can be read AND edited.
      if (
        ref.node_type === "note" ||
        ref.node_type === "ai_output" ||
        ref.node_type === "org"
      ) {
        const node = await api.request<NodeOut>("GET", `/api/nodes/${ref.id}`);
        useTabs.getState().openMarkdown(ref.id, node.label);
        onNavigate?.();
        return;
      }
      useTabs.getState().activate("graph");
      useGraph.getState().requestFocus(ref.id);
      useGraph.getState().select({ kind: "node", id: ref.id });
      onNavigate?.();
    } catch (e) {
      toast.error("Could not open", { description: String(e) });
    }
  };

  return (
    <div>
      <button
        onClick={() => (entry.dir ? setExpanded((v) => !v) : void openFile())}
        title={entry.relpath}
        className={cn(
          "group flex w-full items-center gap-1.5 rounded-sm py-[3px] pr-1 text-left",
          "hover:bg-accent",
          entry.dir ? "text-muted-foreground" : entry.ref ? "text-muted-foreground" : "text-muted-foreground",
        )}
        style={{ paddingLeft: 6 + depth * 14 }}
      >
        {entry.dir ? (
          <>
            {expanded ? (
              <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            {expanded ? (
              <FolderOpen className="h-3.5 w-3.5 shrink-0 text-primary/60" />
            ) : (
              <Folder className="h-3.5 w-3.5 shrink-0 text-primary/60" />
            )}
          </>
        ) : (
          <span className="ml-[12px] flex shrink-0">{fileIcon(entry)}</span>
        )}
        <span
          className={cn(
            "truncate text-[12px] leading-tight group-hover:text-foreground",
            entry.dir && "font-medium",
          )}
        >
          {entry.name}
        </span>
      </button>
      {entry.dir && expanded && (
        <div className="relative">
          {/* VS-Code-style indent guide */}
          <span
            className="pointer-events-none absolute bottom-0 top-0 w-px bg-border/70"
            style={{ left: 11 + depth * 14 }}
          />
          {(entry.children ?? []).map((c) => (
            <TreeRow key={c.relpath} entry={c} depth={depth + 1} onNavigate={onNavigate} />
          ))}
          {(entry.children ?? []).length === 0 && (
            <p
              className="py-0.5 text-[11px] italic text-muted-foreground/60"
              style={{ paddingLeft: 24 + depth * 14 }}
            >
              empty
            </p>
          )}
        </div>
      )}
    </div>
  );
}
